import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { MapPin, Skull, Thermometer, Drop, Leaf, Lightning, ArrowRight, Sparkle, CheckCircle, SpinnerGap, Gift, CaretDown, CaretUp } from '@phosphor-icons/react';
import { API_URL } from '../config/api';
import { SEO } from '../components/SEO';
import { HiTexProductsFooter } from '../components/HiTexProductsFooter';
import { trackEvent } from '../components/Analytics';

const BAND = {
  minimal: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-400',
  low:     'bg-sky-500/10 border-sky-500/30 text-sky-700 dark:text-sky-400',
  moderate:'bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-400',
  high:    'bg-red-500/10 border-red-500/30 text-red-700 dark:text-red-400',
  unknown: 'bg-muted/30 border-border text-muted-foreground',
};

const HeyPage = () => {
  const navigate = useNavigate();
  const [snapshot, setSnapshot] = useState(null);
  const [status, setStatus] = useState('asking'); // asking | loading | loaded | zip | error
  const [zip, setZip] = useState('');
  const [email, setEmail] = useState('');
  const [grass, setGrass] = useState('');
  const [captured, setCaptured] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Fire on mount — ask for GPS immediately
  useEffect(() => {
    if (!('geolocation' in navigator)) {
      setStatus('zip');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        await loadSnapshot({ lat: latitude, lng: longitude });
      },
      () => setStatus('zip'),
      { timeout: 10000, enableHighAccuracy: false },
    );
  }, []); // eslint-disable-line

  const loadSnapshot = async (params) => {
    setStatus('loading');
    try {
      const q = new URLSearchParams(
        Object.entries(params).reduce((acc, [k, v]) => {
          if (v !== null && v !== undefined && v !== '') acc[k] = v;
          return acc;
        }, {}),
      ).toString();
      const res = await axios.get(`${API_URL}/api/public/local/snapshot?${q}`);
      setSnapshot(res.data);
      setStatus('loaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load your local snapshot.');
      setStatus('zip');
    }
  };

  const handleZipSubmit = (e) => {
    e.preventDefault();
    if (!zip || zip.length < 4) {
      toast.error('Enter a valid ZIP');
      return;
    }
    loadSnapshot({ zip });
  };

  const handleEmail = async (e) => {
    e.preventDefault();
    if (!email.includes('@')) {
      toast.error('Enter a valid email');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/public/local/lead`, {
        email: email.trim().toLowerCase(),
        source: 'hey-page',
        lat: snapshot?.location?.lat,
        lng: snapshot?.location?.lng,
        zip: snapshot?.location?.zip || zip,
        city: snapshot?.location?.city,
        region: snapshot?.location?.region,
        grass_interest: grass,
      });
      setCaptured(true);
      toast.success("You're in — we'll send your local lawn forecast weekly.");
      // GA4: NFC → /hey → email conversion. The single most important funnel event.
      trackEvent('lead_captured', {
        source: 'hey-page',
        zip: snapshot?.location?.zip || zip || '',
        city: snapshot?.location?.city || '',
        region: snapshot?.location?.region || '',
        grass_interest: grass || '',
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went sideways — try again?');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="hey-page">
      <SEO
        title="Your Local Lawn Snapshot — TurfPulse"
        description="See what's happening on your lawn right now — disease pressure, soil temp, next action — based on your exact ZIP."
      />

      {/* Top strip — "you met Derwin" */}
      <header className="border-b border-border/60 bg-card/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-3xl mx-auto flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <Leaf weight="fill" size={20} className="text-primary" />
            <span className="text-sm font-bold tracking-tight">TurfPulse</span>
          </div>
          <Button size="sm" variant="ghost" onClick={() => navigate('/')} data-testid="hey-main-site-btn">
            Full site <ArrowRight size={13} className="ml-1" />
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 sm:py-12 space-y-8">
        {/* Personal hero */}
        <div className="space-y-3" data-testid="hey-hero">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium">
            <Sparkle size={12} weight="fill" /> You met Derwin
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">
            Thanks for tapping. Here's what TurfPulse does — <span className="text-primary">right now, for your lawn</span>.
          </h1>
          <p className="text-base text-muted-foreground">
            I built TurfPulse because generic lawn advice doesn't cut it. Your grass doesn't care about a calendar — it cares about your soil temp, your humidity, your last mow. Below is what's happening at your exact location, pulled live from the same engine our paid users see.
          </p>
        </div>

        {/* Status / Snapshot */}
        {status === 'asking' && (
          <Card className="border-2 border-dashed border-border">
            <CardContent className="p-8 text-center space-y-2">
              <MapPin size={32} weight="duotone" className="mx-auto text-primary" />
              <p className="text-sm font-medium">Asking your browser for location…</p>
              <p className="text-xs text-muted-foreground">Tap "Allow" to see live data. Or skip and enter your ZIP.</p>
              <Button variant="link" size="sm" onClick={() => setStatus('zip')} data-testid="hey-use-zip">
                Use ZIP instead
              </Button>
            </CardContent>
          </Card>
        )}

        {status === 'loading' && (
          <Card>
            <CardContent className="p-8 text-center space-y-2">
              <SpinnerGap size={28} className="animate-spin mx-auto text-primary" />
              <p className="text-sm text-muted-foreground">Pulling your live lawn conditions…</p>
            </CardContent>
          </Card>
        )}

        {status === 'zip' && (
          <Card data-testid="hey-zip-fallback">
            <CardContent className="p-5 space-y-3">
              <p className="text-sm">
                We'll use your ZIP to pull live weather + soil conditions for your area.
              </p>
              <form onSubmit={handleZipSubmit} className="flex gap-2">
                <Input
                  type="text"
                  inputMode="numeric"
                  maxLength={5}
                  value={zip}
                  onChange={(e) => setZip(e.target.value.replace(/\D/g, ''))}
                  placeholder="78664"
                  className="font-mono"
                  data-testid="hey-zip-input"
                />
                <Button type="submit" data-testid="hey-zip-submit">Show me</Button>
              </form>
            </CardContent>
          </Card>
        )}

        {status === 'loaded' && snapshot && (
          <div className="space-y-5" data-testid="hey-snapshot">
            {/* Next action — the money line */}
            <div className="rounded-xl border-2 border-primary/40 bg-primary/5 p-5 sm:p-6" data-testid="hey-next-action">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-primary mb-1 flex items-center gap-1.5">
                <Lightning size={12} weight="fill" /> TurfPulse says
              </div>
              <p className="text-base sm:text-lg font-medium leading-snug">{snapshot.next_action}</p>
            </div>

            {/* Live tiles */}
            <div className="grid grid-cols-2 gap-3">
              <Tile
                icon={<Skull size={16} weight="fill" />}
                label="Dollar Spot pressure"
                value={snapshot.dollar_spot.probability_pct != null ? `${snapshot.dollar_spot.probability_pct}%` : '—'}
                band={snapshot.dollar_spot.band}
                sub="Smith-Kerns 2013"
                testid="hey-tile-ds"
              />
              <Tile
                icon={<Leaf size={16} weight="fill" />}
                label="Large Patch"
                value={snapshot.large_patch.score ? `${snapshot.large_patch.score}/100` : '—'}
                band={snapshot.large_patch.band}
                sub="warm-season"
                testid="hey-tile-lp"
              />
              <Tile
                icon={<Thermometer size={16} weight="fill" />}
                label="Soil temp (2 in)"
                value={snapshot.weather_now.soil_temp_f != null ? `${snapshot.weather_now.soil_temp_f}°F` : '—'}
                sub="live from your ZIP"
                testid="hey-tile-soil"
              />
              <Tile
                icon={<Drop size={16} weight="fill" />}
                label="Avg humidity / rain days"
                value={`${snapshot.weather_now.rh_pct ?? '—'}% · ${snapshot.weather_now.rain_days_7d ?? 0} days`}
                sub="last 7 days"
                testid="hey-tile-weather"
              />
            </div>

            {/* Location footer */}
            <p className="text-[11px] text-muted-foreground text-center">
              Pulled live for{' '}
              {snapshot.location?.city
                ? `${snapshot.location.city}${snapshot.location.region ? ', ' + snapshot.location.region : ''}`
                : `${snapshot.location?.lat?.toFixed(2)}, ${snapshot.location?.lng?.toFixed(2)}`}
              {' · '}via Open-Meteo
            </p>

            {/* Round Rock rebate card — contextual to neighbors */}
            <RebateCard location={snapshot.location} />

            {/* Email capture */}
            {!captured ? (
              <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-primary/0" data-testid="hey-email-card">
                <CardContent className="p-5 sm:p-6 space-y-3">
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-primary mb-1">
                      Want this every week?
                    </div>
                    <h2 className="text-lg sm:text-xl font-bold">Your local lawn forecast, direct to your inbox.</h2>
                    <p className="text-xs text-muted-foreground mt-1">
                      One email a week. Disease pressure for your ZIP, what action's best, zero spam. Unsubscribe anytime.
                    </p>
                  </div>
                  <form onSubmit={handleEmail} className="space-y-2" data-testid="hey-email-form">
                    <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@email.com"
                        required
                        data-testid="hey-email-input"
                      />
                      <Button type="submit" disabled={submitting} data-testid="hey-email-submit">
                        {submitting ? 'Signing up…' : 'Send it to me'}
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-1.5" data-testid="hey-grass-picker">
                      {['bermuda', 'st-augustine', 'zoysia', 'fescue', 'kbg', 'ryegrass', 'not sure'].map((g) => (
                        <button
                          key={g}
                          type="button"
                          onClick={() => setGrass(g === grass ? '' : g)}
                          className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${
                            g === grass
                              ? 'bg-primary text-primary-foreground border-primary'
                              : 'bg-muted/30 border-border text-muted-foreground hover:border-primary/40'
                          }`}
                          data-testid={`hey-grass-${g}`}
                        >
                          {g}
                        </button>
                      ))}
                    </div>
                    <p className="text-[10px] text-muted-foreground">Optional — lets Moe tune advice to your grass.</p>
                  </form>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-emerald-500/40 bg-emerald-500/10" data-testid="hey-email-captured">
                <CardContent className="p-5 flex items-start gap-3">
                  <CheckCircle size={20} weight="fill" className="text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold">You're in.</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Your first weekly forecast drops this coming Sunday. In the meantime, explore TurfPulse below.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* What TurfPulse does */}
            <div className="space-y-3 pt-4" data-testid="hey-features">
              <h2 className="text-base font-bold">What else TurfPulse does, in case you're curious:</h2>
              <Feature
                title="Snap a photo of a weed or disease spot — we'll name it."
                desc="AI Photo Diagnosis identifies what's going on and tells you exactly how to fix it."
                cta="Try free — no signup"
                testid="hey-feature-diag"
                onClick={() => navigate('/try')}
              />
              <Feature
                title="A Smart Lawn Plan that updates with your local GDD."
                desc="No calendar-based guessing. Your next action updates as your soil temps move."
                cta="See what a plan looks like"
                testid="hey-feature-plan"
                onClick={() => navigate('/')}
              />
              <Feature
                title="Pro-grade toolkit for lawn nerds."
                desc="Soil test PDF reader · FRAC/HRAC rotation tracker · Ecowitt sensor integration · spoon-feed planner."
                cta="Explore pro tools"
                testid="hey-feature-pro"
                onClick={() => navigate('/')}
              />
            </div>

            {/* Primary CTA */}
            <div className="flex flex-col sm:flex-row gap-2 pt-4">
              <Button
                className="flex-1"
                size="lg"
                onClick={() => navigate('/signup')}
                data-testid="hey-cta-signup"
              >
                Start a free account →
              </Button>
              <Button
                variant="outline"
                className="flex-1"
                size="lg"
                onClick={() => navigate('/try')}
                data-testid="hey-cta-try"
              >
                Try free diagnosis
              </Button>
            </div>
          </div>
        )}
      </main>

      {/* Minimal footer */}
      <footer className="border-t border-border/60 mt-12 py-6">
        <div className="max-w-3xl mx-auto px-4 text-center space-y-3">
          <div className="text-xs">
            <a href="/kit" className="text-primary font-medium hover:underline" data-testid="hey-kit-link">
              Browse the TurfPulse Lab Starter Kit →
            </a>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Live data via Open-Meteo · No account required to view · derwin@getturfpulse.com
          </p>
          <HiTexProductsFooter />
        </div>
      </footer>
    </div>
  );
};

const Tile = ({ icon, label, value, band, sub, testid }) => (
  <div className={`rounded-lg border p-3 ${band ? BAND[band] || BAND.unknown : 'bg-card border-border/60'}`} data-testid={testid}>
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-1.5">
        {icon}
        <span className="text-[10px] font-semibold uppercase tracking-wider">{label}</span>
      </div>
      {band && <span className="text-[9px] font-semibold uppercase tracking-wider opacity-70">{band}</span>}
    </div>
    <div className="text-xl font-bold font-mono mt-1.5">{value}</div>
    <div className="text-[10px] opacity-70 mt-0.5">{sub}</div>
  </div>
);

const Feature = ({ title, desc, cta, onClick, testid }) => (
  <Card>
    <CardContent className="p-4 flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="text-sm font-semibold">{title}</div>
        <div className="text-xs text-muted-foreground mt-0.5">{desc}</div>
      </div>
      <Button size="sm" variant="outline" onClick={onClick} className="shrink-0" data-testid={testid}>
        {cta}
      </Button>
    </CardContent>
  </Card>
);

// Local rebate intel — contextual perk for Round Rock neighbors.
// Layered reveal: confirm Round Rock zip → verify actual water utility →
// show eligibility. Crossroads / Brushy Creek MUD / other MUDs do NOT
// participate in the city rebate (confirmed by phone with Crossroads rep
// Feb 2026). Saves neighbors from filing a denial.
const UTILITIES = [
  { key: 'round_rock', label: 'City of Round Rock Water Utilities', hint: 'Bill says "City of Round Rock"', qualifies: true },
  { key: 'crossroads', label: 'Crossroads Utility Services', hint: 'Manages MUDs — not city water', qualifies: false, reason: 'Crossroads confirmed (Feb 2026) they do not participate in any conservation rebate programs.' },
  { key: 'brushy_creek_mud', label: 'Brushy Creek MUD / Jonah Water SUD / Manville WSC', hint: 'Neighborhood MUD or WSC', qualifies: false, reason: 'Most Central TX MUDs / WSCs do not offer smart irrigation rebates. Check your billing entity directly.' },
  { key: 'unsure', label: 'Not sure who bills me', hint: 'Check your water bill', qualifies: null },
];

const RebateCard = ({ location }) => {
  const [open, setOpen] = useState(false);
  const [utility, setUtility] = useState(null); // null | one of UTILITIES keys
  const city = (location?.city || '').toLowerCase();
  const region = (location?.region || '').toLowerCase();
  const isRoundRock = city.includes('round rock');
  const isTexas = region === 'tx' || region === 'texas';

  if (!isRoundRock && !isTexas) return null;

  if (!isRoundRock) {
    return (
      <Card className="border-emerald-500/30 bg-emerald-500/5" data-testid="hey-rebate-nudge">
        <CardContent className="p-4 flex items-start gap-3">
          <Gift size={18} weight="fill" className="text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-sm font-semibold">Heads up — your city may pay you to upgrade.</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Most Central Texas water utilities (Austin, Cedar Park, Georgetown, Pflugerville, Round Rock) offer rebates on smart controllers + sensors. Search your utility's website for <em>"smart irrigation rebate"</em> before you buy.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Round Rock ZIP — but we don't yet know the actual utility
  const chosen = UTILITIES.find((u) => u.key === utility);

  // Step 1: show the utility picker
  if (!chosen) {
    return (
      <Card className="border-2 border-emerald-500/40 bg-gradient-to-br from-emerald-500/10 to-emerald-500/0" data-testid="hey-rebate-picker">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-start gap-3">
            <Gift size={22} weight="fill" className="text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-400 mb-0.5">
                Round Rock Neighbor Perk · Eligibility Check
              </div>
              <h3 className="text-base sm:text-lg font-bold leading-snug">
                Before you buy — who actually bills you for water?
              </h3>
              <p className="text-xs text-muted-foreground mt-1">
                Round Rock ZIP ≠ Round Rock Water customer. The <strong>$100 smart controller rebate</strong> is only for direct City of Round Rock accounts. Takes 10 seconds to verify and save yourself a filed denial.
              </p>
            </div>
          </div>

          <div className="space-y-2 pt-1" data-testid="hey-rebate-utility-options">
            {UTILITIES.map((u) => (
              <button
                key={u.key}
                type="button"
                onClick={() => setUtility(u.key)}
                className="w-full text-left border border-emerald-500/20 hover:border-emerald-500/50 bg-background/60 rounded-md p-3 transition-colors group"
                data-testid={`hey-rebate-utility-${u.key}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold">{u.label}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">{u.hint}</div>
                  </div>
                  <ArrowRight size={14} className="shrink-0 text-muted-foreground group-hover:text-emerald-600 transition-colors" />
                </div>
              </button>
            ))}
          </div>

          <p className="text-[11px] text-muted-foreground italic pt-1">
            💡 Your water bill shows your utility in the top-left corner. If it says "Crossroads Utility Services," you're on MUD water — city rebate doesn't apply.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Step 2a: Non-qualifier (Crossroads / MUD / unsure)
  if (chosen.qualifies === false || chosen.qualifies === null) {
    return (
      <Card className="border-2 border-amber-500/40 bg-gradient-to-br from-amber-500/10 to-amber-500/0" data-testid="hey-rebate-non-qualifier">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-start gap-3">
            <Gift size={22} weight="fill" className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400 mb-0.5">
                {chosen.qualifies === null ? 'Check your bill first' : 'No rebate — but still worth upgrading'}
              </div>
              <h3 className="text-base sm:text-lg font-bold leading-snug">
                {chosen.qualifies === null
                  ? "Let's find out who your utility actually is."
                  : `${chosen.label} doesn't offer a smart irrigation rebate.`}
              </h3>
              {chosen.reason && (
                <p className="text-xs text-muted-foreground mt-1">{chosen.reason}</p>
              )}
            </div>
          </div>

          {chosen.qualifies === null ? (
            <div className="space-y-2 text-xs">
              <p className="font-semibold">Two ways to figure it out in 60 seconds:</p>
              <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                <li>Grab your latest water bill. Top-left corner shows the utility name + logo.</li>
                <li>Or call the Round Rock Water main line: <strong>(512) 218-5500</strong> — tell them your address, they'll confirm if you're on city water or a MUD.</li>
              </ol>
              <Button variant="outline" size="sm" className="mt-2 border-amber-500/40" onClick={() => setUtility(null)} data-testid="hey-rebate-reset">
                Re-check after I find out
              </Button>
            </div>
          ) : (
            <div className="bg-background/60 border border-amber-500/20 rounded-md p-3 text-xs space-y-2" data-testid="hey-rebate-alternatives">
              <div>
                <div className="font-semibold text-sm mb-1">Alternatives that still save you money:</div>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  <li><strong>Business write-off</strong> — if you run any LLC/side-hustle and use it for work (landscaping, property management, content), a smart controller is a legitimate deductible equipment expense. Save the receipt.</li>
                  <li><strong>Call your HOA</strong> — some Round Rock HOAs bulk-subsidize conservation upgrades for their residents.</li>
                  <li><strong>Wait for manufacturer sale</strong> — Rachio and Hunter both run 15-25% off around Memorial Day + July 4th.</li>
                  <li><strong>Water savings pay back in 12–18 months</strong> regardless — Central TX ET losses + overwatering habits mean a smart controller typically saves $120–$250/yr on water alone.</li>
                </ul>
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={() => setUtility(null)} data-testid="hey-rebate-back">
              ← Pick a different utility
            </Button>
            <a href="https://www.roundrocktexas.gov/departments/utilities/water-conservation/" target="_blank" rel="noopener noreferrer" className="flex-1">
              <Button variant="outline" size="sm" className="w-full border-amber-500/40">
                City conservation site (for reference)
              </Button>
            </a>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Step 2b: Qualifier — full Round Rock rebate card
  return (
    <Card className="border-2 border-emerald-500/40 bg-gradient-to-br from-emerald-500/10 to-emerald-500/0" data-testid="hey-rebate-round-rock">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-start gap-3">
          <Gift size={22} weight="fill" className="text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-400 mb-0.5 flex items-center gap-2">
              <span>Verified: {chosen.label}</span>
              <button type="button" onClick={() => setUtility(null)} className="underline hover:no-underline" data-testid="hey-rebate-change-utility">
                change
              </button>
            </div>
            <h3 className="text-base sm:text-lg font-bold leading-snug">
              ✅ You qualify. The city will pay up to <span className="text-emerald-700 dark:text-emerald-400">$500/yr</span> toward your smart irrigation upgrade.
            </h3>
            <p className="text-xs text-muted-foreground mt-1">
              As a direct Round Rock water customer with an existing sprinkler system, you're eligible for the <strong>Smart Irrigation Rebate</strong>. Stack this before you buy.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs" data-testid="hey-rebate-breakdown">
          <div className="rounded-md border border-emerald-500/20 bg-background/60 p-2.5">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-400">Smart controller</div>
            <div className="text-sm font-bold mt-0.5">50% back · up to $100</div>
            <div className="text-[10px] text-muted-foreground mt-0.5">Rachio 3, Hydrawise — must be WaterSense certified</div>
          </div>
          <div className="rounded-md border border-emerald-500/20 bg-background/60 p-2.5">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-400">Sensors</div>
            <div className="text-sm font-bold mt-0.5">50% back · up to $50</div>
            <div className="text-[10px] text-muted-foreground mt-0.5">Rain, soil moisture, freeze — combined cap</div>
          </div>
          <div className="rounded-md border border-emerald-500/20 bg-background/60 p-2.5">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-400">MP Rotator swap</div>
            <div className="text-sm font-bold mt-0.5">50% back · no cap</div>
            <div className="text-[10px] text-muted-foreground mt-0.5">Rotary / efficient nozzle conversion</div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="text-xs font-medium text-emerald-700 dark:text-emerald-400 hover:underline flex items-center gap-1"
          data-testid="hey-rebate-toggle"
        >
          {open ? 'Hide the fine print' : 'Show me the fine print'}
          {open ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />}
        </button>

        {open && (
          <div className="text-xs text-muted-foreground space-y-1.5 pt-1 border-t border-emerald-500/20" data-testid="hey-rebate-details">
            <p>• <strong>Max $500/account/year</strong> — combined across all upgrades.</p>
            <p>• Must be a <strong>direct Round Rock water customer</strong> in good standing with an existing irrigation system.</p>
            <p>• Drip conversion pays <strong>$50/zone</strong> · zone cap-off pays <strong>$25/zone</strong> · full decommissioning is a flat <strong>$500</strong>.</p>
            <p>• Irrigation checkup + repairs: <strong>50% back up to $200</strong> (must use a TCEQ-licensed irrigator).</p>
            <p>• <strong>Rain sensor is required</strong> at every controller — the city rebates new ones too.</p>
            <p>• Submit application + itemized receipts within <strong>90 days</strong> of purchase to <code className="text-[11px] bg-muted/50 px-1 rounded">jwoods@roundrocktexas.gov</code>.</p>
            <p>• No pre-inspection required. Check arrives 2–4 weeks after approval.</p>
            <p className="pt-1 italic">Not city-affiliated — just a neighbor pointing you at free money. Verify details on the city's site before you buy.</p>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-2 pt-1">
          <a
            href="https://www.roundrocktexas.gov/wp-content/uploads/2025/09/Smart-Irrigation-Rebate-_Oct-2025.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1"
            data-testid="hey-rebate-pdf-link"
          >
            <Button variant="outline" size="sm" className="w-full border-emerald-500/40 hover:bg-emerald-500/10">
              Open the official PDF <ArrowRight size={13} className="ml-1" />
            </Button>
          </a>
          <a
            href="https://www.roundrocktexas.gov/departments/utilities/water-conservation/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1"
            data-testid="hey-rebate-city-link"
          >
            <Button variant="outline" size="sm" className="w-full border-emerald-500/40 hover:bg-emerald-500/10">
              City water conservation site
            </Button>
          </a>
        </div>
      </CardContent>
    </Card>
  );
};

export default HeyPage;
